import { extractTextFromDocx } from './docxTextExtract';

/**
 * Client-side resume PDF → work-profile form draft (no external API).
 * Uses pdfjs for text extraction + heuristics for common CV sections.
 */

export type ResumeProfileType = 'performer' | 'technical' | 'voiceover' | 'content_creator';

export interface ParsedResumeDraft {
    stage_name?: string;
    about?: string;
    profile_type?: { label: string; value: ResumeProfileType };
    skills?: Array<{
        skill: string;
        expertise: { label: string; value: string };
        years_of_experience?: number;
        description?: string;
    }>;
    other_skills?: Array<{
        skill: string;
        expertise: { label: string; value: string };
        years_of_experience?: number;
        description?: string;
    }>;
    special_skills?: string[];
    language_spoken?: string[];
}

const SECTION_HEADERS =
    /^(summary|profile|objective|about(\s+me)?|experience|work\s+experience|employment|professional\s+experience|education|skills|core\s+competencies|technical\s+skills|special\s+skills|languages?|credits|filmography|projects|certifications?|awards?)\s*:?\s*$/i;

function normalizeLine(line: string): string {
    return line.replace(/\s+/g, ' ').trim();
}

function splitSections(text: string): Record<string, string> {
    const lines = text.split(/\r?\n/).map(normalizeLine).filter(Boolean);

    const sections: Record<string, string[]> = { _preamble: [] };
    let current = '_preamble';

    for (const line of lines) {
        if (SECTION_HEADERS.test(line)) {
            const key = line.replace(/:$/, '').toLowerCase();
            current = key;
            if (!sections[current]) sections[current] = [];
            continue;
        }
        if (!sections[current]) sections[current] = [];
        sections[current].push(line);
    }

    return Object.fromEntries(Object.entries(sections).map(([k, v]) => [k, v.join('\n')]));
}

function pickSection(sections: Record<string, string>, aliases: string[]): string {
    for (const alias of aliases) {
        const found = Object.keys(sections).find(k => k.includes(alias));
        if (found && sections[found]?.trim()) return sections[found].trim();
    }
    return '';
}

function extractName(preamble: string, fullText: string): string | undefined {
    const firstLines = (preamble || fullText)
        .split('\n')
        .map(normalizeLine)
        .filter(l => l && !/@/.test(l) && !/\d{3}[-.\s]?\d{3}/.test(l) && l.length < 60);

    const candidate = firstLines[0];
    if (!candidate) return undefined;
    // Avoid treating section-like or all-caps job titles as names when too long
    if (SECTION_HEADERS.test(candidate)) return undefined;
    return candidate.slice(0, 40);
}

function extractSkills(skillsText: string): string[] {
    if (!skillsText) return [];
    const raw = skillsText
        .split(/[,•·|/\n]+/)
        .map(s => s.replace(/^[-–—*\d.)\s]+/, '').trim())
        .filter(s => s.length > 1 && s.length < 48 && !SECTION_HEADERS.test(s));

    const unique: string[] = [];
    for (const s of raw) {
        const lower = s.toLowerCase();
        if (!unique.some(u => u.toLowerCase() === lower)) unique.push(s);
        if (unique.length >= 12) break;
    }
    return unique;
}

function extractCredits(
    experienceText: string,
): Array<{ title: string; role?: string; year?: string; description?: string }> {
    if (!experienceText) return [];
    const blocks = experienceText.split(/\n(?=[A-Z])/).filter(b => b.trim().length > 8);
    const credits: Array<{ title: string; role?: string; year?: string; description?: string }> = [];

    for (const block of blocks.slice(0, 8)) {
        const lines = block.split('\n').map(normalizeLine).filter(Boolean);
        if (!lines.length) continue;
        const yearMatch = block.match(/\b(19|20)\d{2}\b/);
        const title = lines[0].slice(0, 80);
        const role = lines[1]?.slice(0, 80);
        credits.push({
            title,
            role: role && role !== title ? role : undefined,
            year: yearMatch?.[0],
            description: lines.slice(2, 5).join(' ').slice(0, 280) || undefined,
        });
    }
    return credits;
}

function guessProfileType(text: string, hint?: ResumeProfileType): ResumeProfileType {
    if (hint) return hint;
    const t = text.toLowerCase();
    if (/\b(voice[\s-]?over|vo artist|narrat)/.test(t)) return 'voiceover';
    if (/\b(director of photography|cinematograph|gaffer|grip|editor|camera|sound mixer|technical)\b/.test(t)) {
        return 'technical';
    }
    if (/\b(influencer|content creator|ugc|tiktok|youtube creator)\b/.test(t)) return 'content_creator';
    return 'performer';
}

function mapGigTypeToProfileType(gigType?: string): ResumeProfileType | undefined {
    if (!gigType) return undefined;
    if (gigType === 'cast') return 'performer';
    if (gigType === 'crew' || gigType === 'technical') return 'technical';
    return undefined;
}

export async function extractTextFromPdf(file: File): Promise<string> {
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
    // Bundled worker — no external parse API
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    pdfjs.GlobalWorkerOptions.workerSrc = require('pdfjs-dist/legacy/build/pdf.worker.entry');

    const data = new Uint8Array(await file.arrayBuffer());
    const doc = await pdfjs.getDocument({ data }).promise;
    const pages: string[] = [];

    for (let i = 1; i <= doc.numPages; i += 1) {
        const page = await doc.getPage(i);
        const content = await page.getTextContent();
        const strings = content.items.map((item: { str?: string }) => item.str ?? '').filter(Boolean);
        pages.push(strings.join(' '));
    }

    // Re-join with newlines where pdf often collapses; keep some structure via multiple spaces → newline heuristics
    return pages
        .join('\n\n')
        .replace(/ {2,}/g, '\n')
        .replace(/\n{3,}/g, '\n\n');
}

function isPdf(file: File): boolean {
    const name = file.name.toLowerCase();
    return file.type === 'application/pdf' || name.endsWith('.pdf');
}

function isDocx(file: File): boolean {
    const name = file.name.toLowerCase();
    return (
        name.endsWith('.docx') ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
}

export function mapResumeTextToDraft(text: string, options?: { gigType?: string }): ParsedResumeDraft {
    const sections = splitSections(text);
    const preamble = sections._preamble || '';
    const about =
        pickSection(sections, ['summary', 'profile', 'objective', 'about']) ||
        preamble.split('\n').slice(1, 6).join(' ').slice(0, 600);

    const skillsText = pickSection(sections, ['skill', 'competenc', 'technical skill']);
    const specialText = pickSection(sections, ['special skill']);
    const langText = pickSection(sections, ['language']);
    const experienceText = pickSection(sections, ['experience', 'employment', 'credit', 'filmograph', 'project']);

    const skillNames = extractSkills(skillsText || specialText);
    const specialSkills = extractSkills(specialText);
    const languages = extractSkills(langText)
        .map(l => l.toLowerCase())
        .filter(l => l.length < 30)
        .slice(0, 8);

    const profileType = guessProfileType(text, mapGigTypeToProfileType(options?.gigType));
    const stageName = extractName(preamble, text);
    const credits = extractCredits(experienceText);

    const aboutWithCredits =
        about ||
        (credits?.length
            ? credits
                  .slice(0, 3)
                  .map(c => [c.title, c.role, c.year].filter(Boolean).join(' — '))
                  .join('\n')
            : undefined);

    return {
        ...(stageName ? { stage_name: stageName } : {}),
        ...(aboutWithCredits ? { about: aboutWithCredits.slice(0, 2000) } : {}),
        profile_type: { label: profileType, value: profileType },
        ...(skillNames.length
            ? {
                  skills: skillNames.slice(0, 8).map(skill => ({
                      skill,
                      expertise: { label: 'mid-level', value: 'mid-level' },
                  })),
              }
            : {}),
        ...(specialSkills.length ? { special_skills: specialSkills.slice(0, 10) } : {}),
        ...(languages.length ? { language_spoken: languages } : {}),
    };
}

export async function parseResumeFile(file: File, options?: { gigType?: string }): Promise<ParsedResumeDraft> {
    let text = '';
    if (isPdf(file)) {
        text = await extractTextFromPdf(file);
    } else if (isDocx(file)) {
        text = await extractTextFromDocx(file);
    } else {
        throw new Error('Please upload a PDF or DOCX resume');
    }

    if (!text.trim()) {
        throw new Error('Could not read text from this file. Try another PDF/DOCX or create a resume manually.');
    }
    return mapResumeTextToDraft(text, options);
}
